import React from 'react';
import Box from '@mui/material/Box';
import NavBar from './NavBar';
import RibbonBar from './RibbonBar';
import LoadingOverlay from './LoadingOverlay';
import { InputPanel } from '@/features/inputs';
import { DualPlotPanel } from '@/features/plots';
import OutputPanel from '@/features/output/OutputPanel';
import { GeometryPanel } from '@/features/geometry';
import { useTrainingData, useBetaDialog } from '@/hooks';
import {
  OODWarningDialog,
  LengthLimitDialog,
  ConstraintDialog,
  C1SnubberDialog,
  BetaTestingDialog,
  ExploreDialog,
  MoreDetailsDialog,
  ExploreInfoDialog,
  MarcInfoDialog,
  SyneraInfoDialog,
  AppInfoDialog,
} from '@/features/dialogs';

/**
 * AppShell — Root layout component.
 *
 * Renders a fixed-viewport shell with:
 *  - NavBar (top, outside the grid)
 *  - A CSS Grid body with 3 columns and 2 rows:
 *      Row 1: Ribbon bars (36 px)
 *      Row 2: Main content panes (remaining height)
 *
 * Grid columns: 22% (min 340 px) │ 1fr │ 33%
 *
 * Mirrors the original Python layout:
 *   self.main_grid  grid-template-columns: 22% 1fr 33%
 *                   grid-template-rows:    36px 1fr
 */
const AppShell: React.FC = () => {
  // Load training data on mount and whenever bushing type changes
  useTrainingData();

  // Trigger beta dialog when user selects a beta bushing type
  useBetaDialog();

  return (
  <Box
    sx={{
      position: 'fixed',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundImage: 'url(/assets/jpeg_2.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    }}
  >
    {/* Global loading overlay */}
    <LoadingOverlay />

    {/* Top navigation bar */}
    <NavBar />

    {/* Main grid area */}
    <Box
      sx={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          lg: 'minmax(300px, 2.3fr) 2.5fr 3fr',
        },
        gridTemplateRows: {
          xs: 'auto',
          lg: '36px 1fr',
        },
        gap: '12px',
        p: '12px',
        overflow: { xs: 'auto', lg: 'hidden' },
      }}
    >
      {/* ── Row 1: Ribbon bars ────────────────────────────── */}
      <RibbonBar />

      {/* ── Row 2, Column 1: Input panel (Card 1) ────────── */}
      <Box sx={{ gridColumn: { xs: '1', lg: '1' }, gridRow: { xs: 'auto', lg: '2' }, overflow: 'hidden' }}>
        <InputPanel />
      </Box>

      {/* ── Row 2, Column 2: Plots (Card 2) ──────────────── */}
      <Box
        sx={{
          gridColumn: { xs: '1', lg: '2' },
          gridRow: { xs: 'auto', lg: '2' },
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          overflow: 'hidden',
        }}
      >
        <DualPlotPanel />
      </Box>

      {/* ── Row 2, Column 3: Output + Geometry (Card 3) ──── */}
      <Box
        sx={{
          gridColumn: { xs: '1', lg: '3' },
          gridRow: { xs: 'auto', lg: '2' },

          display: 'grid',
          gridTemplateRows: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: '12px',

          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <OutputPanel />
        <GeometryPanel />
      </Box>
    </Box>

    {/* ── Global dialogs (rendered at root, controlled by store) ── */}
    <OODWarningDialog />
    <LengthLimitDialog />
    <ConstraintDialog />
    <C1SnubberDialog />
    <BetaTestingDialog />
    <ExploreDialog />
    <MoreDetailsDialog />
    <ExploreInfoDialog />
    <MarcInfoDialog />
    <SyneraInfoDialog />
    <AppInfoDialog />
  </Box>
  );
};

export default AppShell;
